import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, MapPin, ClipboardCheck, Users, Activity, Loader2 } from 'lucide-react';
import { FUEL_TYPES } from '../../mock/mock';
import { api } from '../../lib/api';

const Card = ({ icon: Icon, label, value, change }) => (
  <div className="bg-white border border-[#e8e5de] rounded-xl p-5 lift">
    <div className="flex items-center justify-between mb-4">
      <div className="w-9 h-9 rounded-lg bg-[#f3f0e9] flex items-center justify-center"><Icon size={15} strokeWidth={1.6}/></div>
      {change != null && <span className="text-[11px] text-[#15803d] flex items-center gap-1"><TrendingUp size={11}/> +{change}%</span>}
    </div>
    <p className="font-serif text-[34px] leading-none tabular-nums">{value}</p>
    <p className="text-[12px] text-[#5a5a5a] mt-1.5">{label}</p>
  </div>
);

// Inline SVG sparkline (line) for "Capturas hoy"
const Sparkline = ({ values = [], width = 130, height = 56 }) => {
  if (!values || values.length < 2) {
    return <svg width={width} height={height} aria-hidden="true"/>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true">
      <polyline points={points} fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

// Inline SVG smooth curve for "Estaciones cubiertas" (uses coverage 0..1)
const CoverageCurve = ({ pct = 0, width = 130, height = 56 }) => {
  // synthesize a small ramp ending at pct/100
  const p = Math.max(0, Math.min(100, pct)) / 100;
  const w = width, h = height;
  const start = { x: 0, y: h - 6 };
  const end = { x: w, y: h - (h - 8) * p - 3 };
  const c1 = { x: w * 0.4, y: h - 6 };
  const c2 = { x: w * 0.7, y: end.y + (h - end.y) * 0.6 };
  const d = `M ${start.x} ${start.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={d} fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
  );
};

// Big-card with primary stat + chart slot
const HeroCard = ({ title, badge, badgeColor = 'green', value, valueSub, chart, testid }) => {
  const badgeBg = badgeColor === 'red' ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]';
  return (
    <div className="bg-white border border-[#e8e5de] rounded-2xl p-6 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[14px] text-[#3a3a3a]" data-testid={`${testid}-title`}>{title}</h3>
        {badge && (
          <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${badgeBg}`} data-testid={`${testid}-badge`}>{badge}</span>
        )}
      </div>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="font-serif text-[58px] leading-none tracking-tight tabular-nums" data-testid={`${testid}-value`}>{value}</p>
          {valueSub && <p className="font-serif text-[36px] leading-none tracking-tight tabular-nums text-[#1c1c1c] mt-1" data-testid={`${testid}-subvalue`}>{valueSub}</p>}
        </div>
        <div className="shrink-0 -mb-1">{chart}</div>
      </div>
    </div>
  );
};

// Stacked bars: completed (green) + pending (gray) per hour
const CapturesByHour = ({ data, total, pending }) => {
  const maxStack = Math.max(1, ...data.map(d => (d.completed || 0) + (d.pending || 0)));
  return (
    <div className="bg-white border border-[#e8e5de] rounded-2xl p-6" data-testid="captures-by-hour-card">
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-[22px] tracking-tight leading-tight">Capturas por hora · hoy</h3>
          <p className="text-[12px] text-[#8a8579] mt-0.5">
            <span className="tabular-nums">{total}</span> capturas · <span className="tabular-nums">{pending}</span> estaciones pendientes
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11.5px]">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#10b981]"/> Completadas</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#cfcabe]"/> Pendientes</span>
        </div>
      </div>
      <div className="h-[260px] flex items-stretch gap-2.5" data-testid="captures-by-hour-bars">
        {data.map(d => {
          const stack = (d.completed || 0) + (d.pending || 0);
          const completedH = stack > 0 ? (d.completed / maxStack) * 100 : 0;
          const pendingH = stack > 0 ? (d.pending / maxStack) * 100 : 0;
          return (
            <div key={d.hour} className="flex-1 flex flex-col items-stretch min-w-0" data-testid={`hour-${d.hour}`}>
              <div className="flex-1 flex flex-col justify-end">
                {/* pending (top) */}
                <div className="w-full bg-[#cfcabe] transition-all" style={{ height: `${pendingH}%`, borderTopLeftRadius: 4, borderTopRightRadius: 4 }} title={`${d.pending} pendientes`}/>
                {/* completed (bottom) */}
                <div className="w-full bg-[#10b981] transition-all" style={{ height: `${completedH}%`, borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }} title={`${d.completed} completadas`}/>
              </div>
              <span className="text-[10.5px] text-[#8a8579] tabular-nums text-center mt-1.5">{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/stats/overview'),
      api.get('/prices', { params: { limit: 8 } }),
    ]).then(([s, p]) => {
      setStats(s.data);
      setRecent(p.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  const change = stats.capturesChangePct;
  const changeLabel = change >= 0 ? `+${change}%` : `${change}%`;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h2 className="font-serif text-[40px] leading-tight tracking-tight">Resumen</h2>
        <p className="text-[14px] text-[#5a5a5a] mt-1">Vista global de la operación de captura de precios.</p>
      </div>

      {/* Top KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={MapPin} label="Estaciones registradas" value={stats.totalStations}/>
        <Card icon={ClipboardCheck} label="Capturas totales" value={stats.totalCaptures}/>
        <Card icon={Activity} label="Pendientes de aprobar" value={stats.pendingApproval}/>
        <Card icon={Users} label="Capturadores activos" value={stats.activeCapturers}/>
      </div>

      {/* Hero KPI row · today */}
      <div className="grid md:grid-cols-2 gap-4">
        <HeroCard
          testid="kpi-capturas-hoy"
          title="Capturas hoy"
          badge={changeLabel}
          badgeColor={change >= 0 ? 'green' : 'red'}
          value={stats.capturesToday}
          chart={<Sparkline values={stats.capturesSparkline || []}/>}
        />
        <HeroCard
          testid="kpi-estaciones-cubiertas"
          title="Estaciones cubiertas"
          badge={`${stats.coveragePct}%`}
          badgeColor="green"
          value={stats.stationsCovered}
          valueSub={`/ ${stats.stationsWithSlotToday}`}
          chart={<CoverageCurve pct={stats.coveragePct || 0}/>}
        />
      </div>

      {/* Captures by hour stacked bar */}
      <CapturesByHour
        data={stats.capturesByHour || []}
        total={stats.capturesToday || 0}
        pending={stats.pendingStationsToday || 0}
      />

      {/* Recent activity */}
      <div className="bg-white border border-[#e8e5de] rounded-xl p-6">
        <h3 className="text-[16px] font-medium tracking-tight mb-5">Actividad reciente</h3>
        {stats.recentActivity.length === 0 ? (
          <p className="text-[12px] text-[#8a8579] py-4">Sin actividad reciente.</p>
        ) : (
          <ul className="space-y-4">
            {stats.recentActivity.map(a => (
              <li key={a.id} className="flex items-start gap-3">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.type==='flag'?'bg-[#b91c1c]':a.type==='approval'?'bg-[#15803d]':'bg-[#1c1c1c]'}`}/>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px]"><span className="font-medium">{a.user}</span>{' '}
                    {a.type==='capture' && <>capturó precio en </>}
                    {a.type==='approval' && <>aprobó captura de </>}
                    {a.type==='flag' && <>marcó anomalía en </>}
                    <span className="text-[#3a3a3a]">{a.station}</span>
                  </p>
                  <p className="text-[11px] text-[#8a8579] mt-0.5">hace {formatTimeAgo(a.time)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-medium tracking-tight">Últimas capturas</h3>
          <Link to="/admin/prices" className="text-[12px] text-[#1c1c1c] underline underline-offset-4">Ver todas</Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-[12px] text-[#8a8579] py-6 text-center">Sin capturas aún.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="pb-3 font-normal">Estación</th>
                <th className="pb-3 font-normal">Capturador</th>
                {FUEL_TYPES.map(ft => <th key={ft.id} className="pb-3 font-normal text-right">{ft.name}</th>)}
                <th className="pb-3 font-normal text-right">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {recent.map(p => (
                <tr key={p.id}>
                  <td className="py-3"><p className="font-medium">{p.station_name}</p><p className="text-[11px] text-[#8a8579]">{p.brand}</p></td>
                  <td className="py-3 text-[#3a3a3a]">{p.captured_by_name}</td>
                  {FUEL_TYPES.map(ft => <td key={ft.id} className="py-3 text-right font-mono tabular-nums">{p.prices?.[ft.id] != null ? Number(p.prices[ft.id]).toFixed(2) : '—'}</td>)}
                  <td className="py-3 text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status==='approved'?'bg-[#1c1c1c] text-[#f8f6f1]':p.status==='flagged'?'bg-[#fee2e2] text-[#991b1b]':p.status==='rejected'?'bg-[#e8e5de] text-[#5a5a5a]':'bg-[#fef3c7] text-[#92400e]'}`}>
                      {p.status==='approved'?'Aprobada':p.status==='flagged'?'Marcada':p.status==='rejected'?'Rechazada':'Pendiente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
